import React, { useCallback, useState } from 'react'

import { arrayOf, bool, number, oneOf, shape, string } from 'prop-types'

import useEasytabsVisibility from 'Pods/Shared/Hooks/useEasytabsVisibility'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'
import I18n from 'Pods/Shared/I18n'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import CancelSubscriptionModal from './Components/CancelSubscriptionModal/CancelSubscriptionModal'
import EmptySubscription from './Components/EmptySubscription'
import CancelInfo from './Components/Subscription/Components/CancelInfo'
import PaymentHistory from './Components/Subscription/Components/PaymentHistory'
import Subscription from './Components/Subscription/Subscription'
import SubscriptionStatusBanner from './Components/SubscriptionStatusBanner'
import SwitchPlanModal from './Components/SwitchPlanModal/SwitchPlanModal'
import { PLAN_TYPE, SUBSCRIPTION_STATUS } from './constants'
import useCancelFee from './hooks/useCancelFee'
import useSubscription from './hooks/useSubscription'
import useSubscriptionSettingsTracking from './hooks/useSubscriptionSettingsTracking'
import useSwitchPlanPreview from './hooks/useSwitchPlanPreview'
import { plansShape } from './propTypes'

const USAGE_DISABLED_STATUSES = [
  SUBSCRIPTION_STATUS.CANCELED,
  SUBSCRIPTION_STATUS.REDEMPTION,
  SUBSCRIPTION_STATUS.PAST_DUE,
  SUBSCRIPTION_STATUS.SUSPENDED,
]


function SubscriptionManagement({
  subscription = null,
  paymentHistory = EMPTY_ARRAY,
  subscriptionPromoSection = null,
  locale = 'en',
  subscriptionV16Enabled = false,
  plans = null,
}) {
  const [subscriptionData, setSubscriptionData] = useState(subscription)
  const [isSwitchPlanModalOpen, setIsSwitchPlanModalOpen] = useState(false)
  const {
    feeData, isFeeLoading, feeError, fetchFee, resetFeeData,
  } = useCancelFee({ subscriptionId: subscriptionData?.id })
  const {
    previewData, isPreviewLoading, fetchPreview, resetPreview,
  } = useSwitchPlanPreview()
  const { showError } = useNotifications()

  // Plan-type fact, independent of the flag: monthly subscribers (incl. ones from the v1.6
  // window) must skip the early-cancellation fee fetch even when the flag is off.
  const isMonthlyPlan = subscriptionData?.planType === 'monthly'

  const {
    trackSectionView,
    trackCancelClick,
    trackRenewClick,
    trackRenewSuccess,
    trackRenewFailure,
    trackReactivateClick,
    trackReactivateSuccess,
    trackReactivateFailure,
    trackRetryClick,
  } = useSubscriptionSettingsTracking()

  const isExpired = subscriptionData?.status === SUBSCRIPTION_STATUS.EXPIRED

  const translate = (key, options = {}) =>
    I18n.t(key, { ns: 'rails', lng: locale, ...options })

  const handleTabVisible = useCallback(() => {
    if (subscriptionData) {
      trackSectionView()
    }
  }, [subscriptionData, trackSectionView])

  useEasytabsVisibility('tab-settings-subscription', handleTabVisible)

  const handleRestoreSuccess = useCallback(
    ({ renewalScheduled = false } = {}) => {
      if (!renewalScheduled) {
        setSubscriptionData((prev) => ({
          ...prev,
          canceledAt: null,
          renewsOn: prev.periodEndDate,
        }))
      }

      if (isExpired) {
        trackReactivateSuccess(subscriptionData?.id)
      } else {
        trackRenewSuccess(subscriptionData?.id)
      }
    },
    [isExpired, subscriptionData?.id, trackReactivateSuccess, trackRenewSuccess]
  )

  const handleRestoreError = useCallback(
    (errorMessage) => {
      if (isExpired) {
        trackReactivateFailure(subscriptionData?.id, errorMessage)
      } else {
        trackRenewFailure(subscriptionData?.id, errorMessage)
      }
    },
    [isExpired, subscriptionData?.id, trackReactivateFailure, trackRenewFailure]
  )

  const handleRedirectToPaymentForm = useCallback((redirectUrl) => {
    const fallbackUrl = '/profile/subscription/payment-form'
    window.location.assign(redirectUrl || fallbackUrl)
  }, [])

  const handleCancelPlanSwitchSuccess = useCallback(() => {
    setSubscriptionData((prev) => ({
      ...prev,
      scheduledPlanSwitchTo: null,
      scheduledPlanSwitchAt: null,
      // Solidgate sets canceled_at via webhook when the switch is committed.
      // Clear it optimistically on cancel since restore_subscription undoes that.
      canceledAt: null,
      renewsOn: prev.periodEndDate,
    }))
  }, [])

  const {
    isCancelModalOpen,
    isLoading: isRestoreLoading,
    handleOpenCancelModal,
    handleCloseCancelModal,
    handleCancelSubscription,
    handleRenewSubscription,
    handleReactivateSubscription,
    handleRetryPayment,
    retryState,
    handleCancelPlanSwitch,
    isCancelPlanSwitchLoading,
  } = useSubscription({
    subscriptionData,
    messages: {
      cancelError: translate('subscription_management.cancel_modal.error'),
      restoreSuccess: translate('subscription_management.restore.success'),
      restoreError: translate('subscription_management.restore.error'),
      retryError: translate('subscription_management.retry_payment.error.default'),
      cancelPlanSwitchSuccess: translate('subscription_management.scheduled_switch_banner.cancel_success'),
      cancelPlanSwitchError: translate('subscription_management.scheduled_switch_banner.cancel_error'),
    },
    onRestoreSuccess: handleRestoreSuccess,
    onRestoreError: handleRestoreError,
    onRedirectToPaymentForm: handleRedirectToPaymentForm,
    onCancelPlanSwitchSuccess: handleCancelPlanSwitchSuccess,
  })

  const handleCancelClick = useCallback(async () => {
    trackCancelClick()
    if (!isMonthlyPlan) {
      await fetchFee()
    }
    handleOpenCancelModal()
  }, [trackCancelClick, fetchFee, handleOpenCancelModal, isMonthlyPlan])

  const handleCloseCancel = useCallback(() => {
    resetFeeData()
    handleCloseCancelModal()
  }, [resetFeeData, handleCloseCancelModal])

  const handleRenewClick = useCallback(() => {
    trackRenewClick(subscriptionData?.id)
    handleRenewSubscription()
  }, [subscriptionData?.id, trackRenewClick, handleRenewSubscription])

  const handleReactivateClick = useCallback(() => {
    trackReactivateClick(subscriptionData?.id)
    handleReactivateSubscription()
  }, [subscriptionData?.id, trackReactivateClick, handleReactivateSubscription])

  const handleRetryClick = useCallback(() => {
    trackRetryClick(subscriptionData?.id)
    handleRetryPayment()
  }, [subscriptionData?.id, trackRetryClick, handleRetryPayment])

  const handleSwitchSuccess = useCallback((responseData) => {
    const scheduledTo = responseData?.scheduled_plan_switch_to ?? null
    setSubscriptionData((prev) => ({
      ...prev,
      scheduledPlanSwitchTo: scheduledTo,
      scheduledPlanSwitchAt: responseData?.scheduled_plan_switch_at ?? null,
      // Monthly→Annual takes effect immediately — reflect the new plan in the UI right away
      ...(scheduledTo === PLAN_TYPE.YEARLY && { planType: PLAN_TYPE.YEARLY, billingCycle: PLAN_TYPE.YEARLY }),
    }))
  }, [])

  const handleSwitchPlanClick = useCallback(async () => {
    if (isPreviewLoading || subscriptionData?.scheduledPlanSwitchTo) return
    const currentPlan = subscriptionData?.planType === PLAN_TYPE.MONTHLY
      ? PLAN_TYPE.MONTHLY
      : PLAN_TYPE.YEARLY
    const targetPlan = currentPlan === PLAN_TYPE.YEARLY ? PLAN_TYPE.MONTHLY : PLAN_TYPE.YEARLY
    const data = await fetchPreview({ to: targetPlan })
    if (data) {
      setIsSwitchPlanModalOpen(true)
    } else {
      showError(translate('subscription_management.switch_plan_modal.preview_toast_error'))
    }
  }, [
    isPreviewLoading,
    subscriptionData?.planType,
    subscriptionData?.scheduledPlanSwitchTo,
    fetchPreview,
    showError,
    translate,
  ])

  const handleCloseSwitchPlanModal = useCallback(() => {
    setIsSwitchPlanModalOpen(false)
    resetPreview()
  }, [resetPreview])

  const renderContent = () => {
    if (!subscriptionData) {
      return (
        <EmptySubscription
          locale={locale}
          promoSection={subscriptionPromoSection}
        />
      )
    }

    const isUsageDisabled = USAGE_DISABLED_STATUSES.includes(subscriptionData.status)

    // When Annual→Monthly switch is scheduled, Solidgate internally cancels the subscription
    // (force: false), which sets canceled_at via webhook. Override for display so the card
    // shows Active instead of PENDING_CANCEL while the user is still in the committed period.
    const subscriptionForDisplay = subscriptionData.scheduledPlanSwitchTo === PLAN_TYPE.MONTHLY
      ? { ...subscriptionData, canceledAt: null, renewsOn: subscriptionData.periodEndDate }
      : subscriptionData

    return (
      <>
        <SubscriptionStatusBanner
          subscription={subscriptionForDisplay}
          locale={locale}
          retryState={retryState}
        />

        <Subscription
          subscription={subscriptionForDisplay}
          locale={locale}
          isUsageDisabled={isUsageDisabled}
          onCancel={handleCancelClick}
          isCancelLoading={isFeeLoading}
          onReactivate={handleReactivateClick}
          onRenew={handleRenewClick}
          isRestoreLoading={isRestoreLoading}
          updatePaymentUrl={subscriptionData.updatePaymentUrl}
          onRetryPayment={handleRetryClick}
          retryState={retryState}
          onSwitchPlan={handleSwitchPlanClick}
          subscriptionV16Enabled={subscriptionV16Enabled}
          onCancelPlanSwitch={handleCancelPlanSwitch}
          isCancelPlanSwitchLoading={isCancelPlanSwitchLoading}
        />

        {paymentHistory && paymentHistory.length > 0 && (
          <PaymentHistory
            payments={paymentHistory}
            subscriptionId={subscriptionData.id}
            locale={locale}
          />
        )}

        <CancelInfo
          locale={locale}
          subscriptionV16Enabled={subscriptionV16Enabled}
          planType={subscriptionData.planType}
          gracePeriodEligible={subscriptionData.gracePeriodEligible}
        />
      </>
    )
  }

  return (
    <div className="subscription-management">
      <div className="subscription-management__header">
        <h2 className="subscription-management__title">
          {translate('subscription_management.title')}
        </h2>
        <p className="cgt-legacy-text-default">
          {translate('subscription_management.subtitle')}
        </p>
      </div>
      <div className="subscription-management__content">{renderContent()}</div>
      {subscriptionData && (
        <CancelSubscriptionModal
          isOpen={isCancelModalOpen}
          onClose={handleCloseCancel}
          onSubmit={handleCancelSubscription}
          subscriptionId={subscriptionData.id}
          feeData={feeData}
          feeError={feeError}
          locale={locale}
          subscriptionV16Enabled={subscriptionV16Enabled}
          planType={subscriptionData.planType}
        />
      )}
      {subscriptionData && plans && previewData && (
        <SwitchPlanModal
          isOpen={isSwitchPlanModalOpen}
          onClose={handleCloseSwitchPlanModal}
          subscription={subscriptionData}
          plans={plans}
          previewData={previewData}
          locale={locale}
          onSwitchSuccess={handleSwitchSuccess}
        />
      )}
    </div>
  )
}

SubscriptionManagement.propTypes = {
  subscription: shape({
    id: number,
    billingCycle: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.YEARLY]),
    status: string,
    statusLabel: string,
    price: string,
    renewsOn: string,
    periodEndDate: string,
    paymentMethod: string,
    downloadsUsed: number,
    downloadLimit: number,
    canceledAt: string,
    canceledByAdmin: bool,
    endedOn: string,
    updatePaymentUrl: string,
    planType: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.YEARLY]),
    gracePeriodEligible: bool,
    scheduledPlanSwitchTo: string,
    scheduledPlanSwitchAt: string,
  }),
  paymentHistory: arrayOf(
    shape({
      id: number,
      amountPaid: string,
      taxAmount: string,
      currency: string,
      paymentDate: string,
      status: string,
      statusLabel: string,
      invoiceUrl: string,
      paymentMethod: string,
      billingPeriod: string,
    })
  ),
  subscriptionPromoSection: shape({
    images: shape({
      wave: string.isRequired,
      waveLarge: string.isRequired,
      butterfly: string.isRequired,
      bird: string.isRequired,
      flower: string.isRequired,
    }).isRequired,
    locale: string,
    ctaUrl: string,
    subscriptionFlowEnabled: bool,
    isActiveSubscriber: bool,
  }),
  locale: string,
  subscriptionV16Enabled: bool,
  plans: plansShape,
}

export default SubscriptionManagement
